import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  api,
  formatApiError,
  type InboxMessage,
  type MyReferral,
} from '../api';
import { useAuth } from '../auth';
import { useI18n } from '../i18n';
import { DevPremiumToggle } from '../components/DevPanel';
import { paymentsEnabled } from '../config';

export default function Account() {
  const { user, logout, refresh } = useAuth();
  const { t, lang, setLang } = useI18n();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const [avatarUploading, setAvatarUploading] = useState(false);

  if (!user) return null;

  // STA-153 — POST /users/me/avatar replaces the previous localStorage
  // base64 mock. The server resize-crops to 256×256 WEBP, persists to
  // R2 (or local-disk fallback), and stamps users.avatar_url. We dispatch
  // the legacy 'mp-avatar-change' event so the Nav avatar refreshes in
  // sync without a full reload.
  const onAvatarPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    setAvatarError(null);
    if (!f) return;
    if (!/^image\/(jpeg|png|webp)$/.test(f.type)) {
      setAvatarError('僅支援 JPEG / PNG / WebP');
      return;
    }
    if (f.size > 2 * 1024 * 1024) {
      setAvatarError(t('account.avatar_too_large'));
      return;
    }
    setAvatarUploading(true);
    try {
      const updated = await api.uploadAvatar(f);
      await refresh();
      if (updated.avatar_url) {
        window.dispatchEvent(
          new CustomEvent('mp-avatar-change', { detail: updated.avatar_url }),
        );
      }
    } catch (err) {
      setAvatarError(formatApiError(err, '上傳失敗，請稍後再試。'));
    } finally {
      setAvatarUploading(false);
    }
  };

  const sub = user.subscription;
  const canUsePortal = sub.provider === 'stripe';

  const cancel = async () => {
    if (!confirm('確定要取消訂閱嗎？仍可使用到當期結束。')) return;
    setLoading(true);
    setError(null);
    try {
      const r = await api.cancelSubscription();
      setSuccess(`已取消，可使用到 ${new Date(r.cancels_at).toLocaleDateString('zh-TW')}`);
      void refresh();
    } catch (e) {
      setError(formatApiError(e, '取消失敗，請稍後再試。'));
    } finally {
      setLoading(false);
    }
  };

  const openPortal = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.openPortal(`${window.location.origin}/account`);
      window.location.href = r.portal_url;
    } catch (e) {
      setLoading(false);
      setError(formatApiError(e, '開啟付款管理失敗，請稍後再試。'));
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-12">
      <h1 className="mp-display text-4xl text-tarot-gold mb-8">{t('account.title')}</h1>

      <div className="card mb-6 flex items-center gap-5">
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={avatarUploading}
          className="relative w-20 h-20 rounded-full border border-tarot-gold/40 overflow-hidden flex items-center justify-center bg-tarot-bg group flex-none disabled:opacity-60"
          aria-label={t('account.change_avatar')}
        >
          {user.avatar_url ? (
            <img src={user.avatar_url} alt="" className="w-full h-full object-cover" />
          ) : (
            <span className="mp-display text-2xl text-tarot-gold">
              {(user.display_name ?? user.email ?? '?').slice(0, 1).toUpperCase()}
            </span>
          )}
          <span className="absolute inset-0 bg-black/50 text-tarot-gold text-[10px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            {avatarUploading ? '上傳中…' : `📷 ${t('account.change_avatar')}`}
          </span>
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={onAvatarPick}
          className="hidden"
        />
        <div className="min-w-0">
          <div className="text-tarot-muted text-sm mb-1">{t('account.identity')}</div>
          <div className="truncate">{user.display_name ?? user.email ?? user.id}</div>
          {user.email && <div className="text-sm text-tarot-muted mt-1 truncate">{user.email}</div>}
          {avatarError && <p className="text-red-400 text-xs mt-1">{avatarError}</p>}
        </div>
      </div>

      <div className="card mb-6">
        <div className="text-tarot-muted text-sm mb-1">訂閱狀態</div>
        <SubStatus status={sub.status} />
        {sub.provider && (
          <div className="text-xs text-tarot-muted mt-1">
            付款方式：{sub.provider === 'newebpay' ? '藍新（台灣）' : 'Stripe（國際）'}
          </div>
        )}
        {sub.current_period_end && (
          <div className="text-sm text-tarot-muted mt-2">
            當期至 {new Date(sub.current_period_end).toLocaleDateString('zh-TW')}
          </div>
        )}
        {sub.cancels_at && (
          <div className="text-sm text-yellow-300 mt-2">
            已排程取消，將於 {new Date(sub.cancels_at).toLocaleDateString('zh-TW')} 結束
          </div>
        )}

        <div className="mt-6 flex flex-wrap gap-3">
          {sub.status === 'none' && (
            paymentsEnabled ? (
              <Link to="/pricing" className="btn-primary">升級 Premium</Link>
            ) : (
              <button
                type="button"
                disabled
                aria-disabled="true"
                title="付款服務即將上線"
                className="btn-primary opacity-60 cursor-not-allowed"
              >
                升級 Premium（敬請期待）
              </button>
            )
          )}
          {sub.status === 'active' && !sub.cancels_at && (
            <button onClick={cancel} disabled={loading} className="btn-ghost">
              取消訂閱
            </button>
          )}
          {sub.status === 'active' && canUsePortal && (
            <button onClick={openPortal} disabled={loading} className="btn-ghost">
              管理付款方式（Stripe Portal）
            </button>
          )}
        </div>

        {error && <p className="mt-4 text-red-400 text-sm">{error}</p>}
        {success && <p className="mt-4 text-green-400 text-sm">{success}</p>}
      </div>

      <div className="card">
        <div className="text-tarot-muted text-sm mb-1">今日使用</div>
        {sub.status === 'active' ? (
          <div>每日無限抽牌</div>
        ) : (
          <div>
            {user.daily_readings_used ?? 0} / {user.daily_readings_limit ?? 1} 次
          </div>
        )}
        {user.readings_reset_at && sub.status !== 'active' && (
          <div className="text-tarot-muted text-xs mt-1">
            重置時間：{new Date(user.readings_reset_at).toLocaleString('zh-TW', { hour: '2-digit', minute: '2-digit', month: 'short', day: 'numeric' })}
          </div>
        )}
      </div>

      <MailboxSection />

      <ReferralSection />

      <DevPremiumToggle />

      <div className="card mt-6 sm:hidden">
        <div className="text-tarot-muted text-sm mb-3">{t('account.preferences')}</div>
        <div className="flex items-center justify-between gap-3 mb-4">
          <span className="text-sm text-tarot-text">{t('account.language')}</span>
          <div
            className="inline-flex items-center text-[11px] font-medium border border-tarot-gold/30 rounded-full overflow-hidden"
            role="group"
            aria-label="Language switcher"
          >
            <button
              type="button"
              onClick={() => setLang('en')}
              className={`px-3 py-1 ${lang === 'en' ? 'bg-tarot-gold/20 text-tarot-gold' : 'text-tarot-muted hover:text-tarot-gold'}`}
            >
              EN
            </button>
            <span className="w-px h-3 bg-tarot-gold/30" aria-hidden="true" />
            <button
              type="button"
              onClick={() => setLang('zh')}
              className={`px-3 py-1 ${lang === 'zh' ? 'bg-tarot-gold/20 text-tarot-gold' : 'text-tarot-muted hover:text-tarot-gold'}`}
            >
              中
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={logout}
          className="btn-ghost w-full text-sm"
        >
          {t('nav.logout')}
        </button>
      </div>
    </div>
  );
}

// ── Mailbox section (RC-P5) ─────────────────────────────────────────

function MailboxSection() {
  const { refresh } = useAuth();
  const [items, setItems] = useState<InboxMessage[]>([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const reload = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [page, cnt] = await Promise.all([
        api.listMessages({ limit: 10 }),
        api.unreadMessageCount(),
      ]);
      setItems(page.items);
      setUnread(cnt.unread_count);
    } catch (e) {
      // m2 — previously this was a silent reset to empty so users
      // couldn't tell "no messages" from "API failed". Surface the
      // error so they can hit refresh / retry.
      setItems([]);
      setUnread(0);
      setLoadError(formatApiError(e, '載入收件匣失敗，請稍後重新整理。'));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void reload();
  }, []);

  const expand = async (msg: InboxMessage) => {
    setExpandedId(msg.id);
    if (msg.read_at == null) {
      try {
        await api.markMessageRead(msg.id);
        setItems((prev) =>
          prev.map((m) => (m.id === msg.id ? { ...m, read_at: new Date().toISOString() } : m)),
        );
        setUnread((c) => Math.max(0, c - 1));
        // Stardust may have just been credited via grant — refresh user.
        await refresh();
      } catch {
        // ignore — UI optimistically updated
      }
    }
  };

  return (
    <div className="card mt-6">
      <div className="flex items-center justify-between mb-2">
        <div className="text-tarot-muted text-sm">收件匣</div>
        {unread > 0 && (
          <span className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-tarot-gold text-xs text-black">
            {unread}
          </span>
        )}
      </div>
      {loading ? (
        <div className="text-tarot-muted text-sm">載入中…</div>
      ) : loadError ? (
        <div className="text-red-400 text-sm">{loadError}</div>
      ) : items.length === 0 ? (
        <div className="text-tarot-muted text-sm">尚無訊息。</div>
      ) : (
        <ul className="divide-y divide-tarot-purple/15">
          {items.map((m) => (
            <li key={m.id} className="py-2">
              <button
                onClick={() => expand(m)}
                className="w-full text-left flex items-start gap-2 group"
              >
                {m.read_at == null && (
                  <span className="mt-1.5 w-2 h-2 rounded-full bg-tarot-gold flex-none" aria-label="未讀" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span
                      className={`text-sm ${
                        m.read_at == null ? 'text-tarot-text' : 'text-tarot-muted'
                      } truncate`}
                    >
                      {m.title}
                    </span>
                    <span className="text-xs text-tarot-muted flex-none">
                      {new Date(m.created_at).toLocaleDateString('zh-TW', { month: 'short', day: 'numeric' })}
                    </span>
                  </div>
                  {expandedId === m.id && (
                    <div className="mt-2 text-sm text-tarot-text whitespace-pre-wrap break-words">
                      {m.body_md}
                    </div>
                  )}
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


// ── Referral section (RC-P1 / RC-P2) ─────────────────────────────────

function ReferralSection() {
  const { refresh } = useAuth();
  const [data, setData] = useState<MyReferral | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [redeemCode, setRedeemCode] = useState('');
  const [redeeming, setRedeeming] = useState(false);
  const [redeemError, setRedeemError] = useState<string | null>(null);
  const [redeemSuccess, setRedeemSuccess] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const reload = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const r = await api.myReferral();
      setData(r);
    } catch (e) {
      // m2 — surface the failure instead of just showing "推薦碼暫不可
      // 用，請重新整理"; with formatApiError the user sees the backend
      // message (e.g. rate-limit hit) when applicable.
      setData(null);
      setLoadError(formatApiError(e, '推薦資訊載入失敗，請稍後再試。'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void reload();
  }, []);

  const copy = async () => {
    if (!data) return;
    try {
      await navigator.clipboard.writeText(data.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard blocked — fall back to selecting the text
    }
  };

  const submitRedeem = async () => {
    const code = redeemCode.trim().toUpperCase();
    if (!code) return;
    setRedeeming(true);
    setRedeemError(null);
    setRedeemSuccess(null);
    try {
      const r = await api.redeemReferral(code, 'account_page');
      setRedeemSuccess(
        `兌換成功，連續簽到 ${r.grant_required_days} 天後雙方各得 50★。`,
      );
      setRedeemCode('');
      await reload();
      await refresh();
    } catch (e) {
      setRedeemError(formatApiError(e, '兌換失敗，請稍後再試。'));
    } finally {
      setRedeeming(false);
    }
  };

  return (
    <div className="card mt-6">
      <div className="text-tarot-muted text-sm mb-1">推薦獎勵</div>
      {loading ? (
        <div className="text-tarot-muted text-sm">載入中…</div>
      ) : loadError ? (
        <div className="text-red-400 text-sm">{loadError}</div>
      ) : !data ? (
        <div className="text-tarot-muted text-sm">推薦碼暫不可用，請重新整理。</div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3 mt-2">
            <code className="px-3 py-2 rounded bg-tarot-purple/20 text-tarot-gold tracking-widest text-lg">
              {data.code}
            </code>
            <button onClick={copy} className="btn-ghost text-sm">
              {copied ? '已複製推薦碼 ✓' : '複製推薦碼'}
            </button>
          </div>
          <div className="text-tarot-muted text-xs mt-2 break-all">
            {data.share_url}
          </div>

          <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
            <Stat label="累計入帳次數" value={`${data.as_referrer.completed_grants}/${data.as_referrer.completed_grants + data.as_referrer.cap_remaining}`} />
            <Stat label="待入帳" value={String(data.as_referrer.pending_grants)} />
            <Stat label="已賺 Stardust" value={`${data.as_referrer.stardust_earned} ★`} />
            <Stat
              label="作為被推薦人"
              value={
                data.as_referee.granted
                  ? `已入帳 ${data.as_referee.stardust_received}★`
                  : data.as_referee.redeemed
                    ? '兌換中'
                    : '尚未兌換'
              }
            />
          </div>

          {!data.as_referee.redeemed && (
            <div className="mt-5 pt-5 border-t border-tarot-purple/20">
              <div className="text-tarot-muted text-xs mb-2">
                有別人的推薦碼？輸入後雙方在你連續簽到 7 天後各獲得 50★（一次性）。
              </div>
              <div className="flex gap-2">
                <input
                  value={redeemCode}
                  onChange={(e) => setRedeemCode(e.target.value.toUpperCase())}
                  placeholder="ABCDEFGH"
                  maxLength={16}
                  className="flex-1 px-3 py-2 rounded-md bg-tarot-purple/10 border border-tarot-purple/30 text-tarot-text focus:outline-none focus:border-tarot-gold uppercase tracking-widest text-sm"
                />
                <button
                  onClick={submitRedeem}
                  disabled={redeeming || !redeemCode.trim()}
                  className="btn-primary text-sm"
                >
                  {redeeming ? '兌換中…' : '兌換'}
                </button>
              </div>
              {redeemError && (
                <p className="mt-2 text-red-400 text-sm">{redeemError}</p>
              )}
              {redeemSuccess && (
                <p className="mt-2 text-green-400 text-sm">{redeemSuccess}</p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded bg-tarot-purple/10 px-3 py-2">
      <div className="text-tarot-muted text-xs">{label}</div>
      <div className="text-tarot-text">{value}</div>
    </div>
  );
}

function SubStatus({ status }: { status: string }) {
  const map: Record<string, { label: string; cls: string }> = {
    none: { label: '免費版', cls: 'text-tarot-muted' },
    active: { label: 'Premium', cls: 'text-tarot-gold' },
    past_due: { label: '付款失敗', cls: 'text-red-400' },
    cancelled: { label: '已取消', cls: 'text-tarot-muted' },
  };
  const v = map[status] ?? { label: status, cls: '' };
  return <div className={`text-lg ${v.cls}`}>{v.label}</div>;
}
